import React, { useState, useEffect, useCallback } from 'react';
import GalleryItem from './GalleryItem';
import { useColorTheme } from '../../context/ColorThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyGallery - Full gallery page showing all user's generated content
 */
const MyGallery = ({ token, onBack }) => {
  const { colorTheme } = useColorTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ images: 0, videos: 0, total: 0, total_size_mb: 0 });
  
  // Filters and pagination
  const [filter, setFilter] = useState('all'); // 'all', 'image', 'video'
  const [sortBy, setSortBy] = useState('date_desc');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Lightbox state
  const [lightboxItem, setLightboxItem] = useState(null);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Multi-select mode for bulk actions (favorite / unfavorite many at once).
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const ITEMS_PER_PAGE = 24;

  const fetchGallery = useCallback(async (resetItems = false) => {
    if (!token) return;

    try {
      setLoading(true);
      const currentOffset = resetItems ? 0 : offset;
      
      const params = new URLSearchParams({
        limit: ITEMS_PER_PAGE.toString(),
        offset: currentOffset.toString(),
        sort_by: sortBy
      });
      
      if (filter === 'favorites') {
        params.append('favorites_only', 'true');
      } else if (filter !== 'all') {
        params.append('media_type', filter);
      }

      const response = await fetch(`${API_URL}/api/gallery/items?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // Auth error - show empty state
        setItems([]);
        setTotal(0);
        setHasMore(false);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch gallery');
      }

      const data = await response.json();
      
      if (resetItems) {
        setItems(data.items || []);
        setOffset(0);
      } else {
        setItems(prev => [...prev, ...(data.items || [])]);
      }
      
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
      setError(null);
    } catch (err) {
      console.error('Error fetching gallery:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filter, sortBy, offset]);

  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/gallery/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Silently handle auth errors for stats
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchGallery(true);
    fetchStats();
  }, [filter, sortBy]);

  const loadMore = () => {
    setOffset(prev => prev + ITEMS_PER_PAGE);
  };

  useEffect(() => {
    if (offset > 0) {
      fetchGallery(false);
    }
  }, [offset]);

  const handleDelete = async (item) => {
    setDeleteConfirm(item);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      const response = await fetch(
        `${API_URL}/api/gallery/item?item_key=${encodeURIComponent(deleteConfirm.id)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setItems(prev => prev.filter(item => item.id !== deleteConfirm.id));
        setTotal(prev => prev - 1);
        fetchStats();
      }
    } catch (err) {
      console.error('Error deleting item:', err);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await fetch(item.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.filename || `download.${item.extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(item.url, '_blank');
    }
  };

  const handleToggleFavorite = async (item, wantFav) => {
    // Optimistic update — flip locally first, roll back on failure.
    const prevItems = items;
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, is_favorite: wantFav } : x));
    // If we're on the Favorites tab and this was un-favorited, drop it from view.
    if (filter === 'favorites' && !wantFav) {
      setItems(prev => prev.filter(x => x.id !== item.id));
    }
    try {
      const res = await fetch(`${API_URL}/api/gallery/item/favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ item_key: item.id, is_favorite: wantFav }),
      });
      if (!res.ok) throw new Error('Favorite toggle failed');
    } catch (err) {
      console.error(err);
      setItems(prevItems);  // roll back
    }
  };

  const handleBulkFavorite = async (wantFav) => {
    if (selectedKeys.size === 0) return;
    const keys = Array.from(selectedKeys);
    const prevItems = items;
    // Optimistic UI
    setBulkBusy(true);
    setItems(prev => prev.map(x => selectedKeys.has(x.id) ? { ...x, is_favorite: wantFav } : x));
    if (filter === 'favorites' && !wantFav) {
      setItems(prev => prev.filter(x => !selectedKeys.has(x.id)));
    }
    try {
      const res = await fetch(`${API_URL}/api/gallery/items/favorite/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ item_keys: keys, is_favorite: wantFav }),
      });
      if (!res.ok) throw new Error('Batch favorite failed');
    } catch (err) {
      console.error(err);
      setItems(prevItems);
    } finally {
      setBulkBusy(false);
      setSelectedKeys(new Set());
    }
  };

  const toggleSelectItem = (item) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';
  const gradientBg = colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50';
  const activeFilter = colorTheme === 'purple' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : colorTheme === 'blue' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20';
  const sortRing = colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-emerald-500';

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="my-gallery-page">
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                  data-testid="gallery-back-btn"
                >
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div>
                <h1 className={`text-2xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                  My Gallery
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {stats.total} items · {stats.images} images · {stats.videos} videos · {stats.total_size_mb} MB
                </p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              {/* Type Filter */}
              <div className="flex gap-1 bg-slate-100/80 dark:bg-slate-800/50 rounded-xl p-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'image', label: 'Images' },
                  { key: 'video', label: 'Videos' },
                  { key: 'favorites', label: '★ Favorites' }
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                      filter === f.key ? activeFilter : 'text-slate-600 dark:text-slate-300 hover:text-slate-800'
                    }`}
                    data-testid={`gallery-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`px-3 py-2 bg-white/70 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 rounded-xl text-xs text-slate-700 dark:text-slate-200 focus:ring-2 ${sortRing}`}
                data-testid="gallery-sort-select"
              >
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="name">Name</option>
                <option value="size">Size</option>
              </select>

              {/* Multi-select toggle */}
              <button
                onClick={() => {
                  setSelectMode(prev => !prev);
                  setSelectedKeys(new Set());
                }}
                className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
                  selectMode
                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-transparent'
                    : 'bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 border-slate-200/50 dark:border-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                }`}
                data-testid="gallery-select-mode-toggle"
              >
                {selectMode ? 'Done' : 'Select'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar — only appears in select mode */}
      {selectMode && (
        <div className="sticky top-[68px] z-20 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-lg" data-testid="gallery-bulk-actionbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold">{selectedKeys.size} selected</span>
              <button
                onClick={() => setSelectedKeys(new Set(items.map(i => i.id)))}
                className="text-xs opacity-70 hover:opacity-100 underline"
                data-testid="gallery-bulk-select-all"
              >Select all on page</button>
              <button
                onClick={() => setSelectedKeys(new Set())}
                className="text-xs opacity-70 hover:opacity-100 underline"
                data-testid="gallery-bulk-clear"
              >Clear</button>
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={selectedKeys.size === 0 || bulkBusy}
                onClick={() => handleBulkFavorite(true)}
                data-testid="gallery-bulk-favorite"
                className="px-3 py-1.5 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-xs font-bold shadow disabled:opacity-40 flex items-center gap-1.5"
              >
                <span>★</span> Favorite selected
              </button>
              <button
                disabled={selectedKeys.size === 0 || bulkBusy}
                onClick={() => handleBulkFavorite(false)}
                data-testid="gallery-bulk-unfavorite"
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 dark:bg-slate-900/10 dark:hover:bg-slate-900/20 text-xs font-medium disabled:opacity-40"
              >
                ☆ Un-favorite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && items.length === 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-48 bg-slate-200/50 dark:bg-slate-700/50 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">😕</div>
            <p className="text-gray-500 dark:text-gray-400">Failed to load gallery</p>
            <button
              onClick={() => fetchGallery(true)}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🎨</div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {filter === 'all' 
                ? "You haven't created any content yet"
                : `No ${filter}s found`}
            </p>
            <p className="text-slate-400 dark:text-slate-500 mt-1 text-xs">
              Start generating amazing content in the Ad Creator Hub!
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {items.map((item) => (
                <GalleryItem
                  key={item.id}
                  item={item}
                  size="medium"
                  onView={selectMode ? (() => toggleSelectItem(item)) : setLightboxItem}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onToggleFavorite={handleToggleFavorite}
                  selectMode={selectMode}
                  isSelected={selectedKeys.has(item.id)}
                  onToggleSelect={toggleSelectItem}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className={`px-6 py-3 text-white rounded-xl transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                    colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-500/20' :
                    colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20' :
                    'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20'
                  }`}
                  data-testid="gallery-load-more"
                >
                  {loading ? 'Loading...' : `Load More (${total - items.length} remaining)`}
                </button>
              </div>
            )}

            <div className="text-center mt-4 text-xs text-slate-400">
              Showing {items.length} of {total} items
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightboxItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxItem(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightboxItem(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {lightboxItem.type === 'video' ? (
              <video
                src={lightboxItem.url}
                className="w-full max-h-[80vh] rounded-lg"
                controls
                autoPlay
              />
            ) : (
              <img
                src={lightboxItem.url}
                alt={lightboxItem.title}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}
            <div className="mt-4 text-center">
              <p className="text-white font-medium">{lightboxItem.title}</p>
              <p className="text-gray-400 text-sm">{lightboxItem.size_mb} MB • {lightboxItem.extension.toUpperCase()}</p>
              <button
                onClick={() => handleDownload(lightboxItem)}
                className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
              Delete this item?
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              "{deleteConfirm.title}" will be permanently deleted. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyGallery;
